import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  ArrowRight,
  CheckCircle,
  Heart,
  Search,
  CalendarClock,
  ClipboardList,
  MessageSquare,
  HandHelping,
  RefreshCw,
  Building2,
  Users,
  TrendingUp,
} from "lucide-react";
import SEO from "../components/SEO";
import StructuredData from "../components/StructuredData";
import MidPageCTA from "../components/MidPageCTA";

const PatientExperience = () => {
  const services = [
    {
      icon: Search,
      title: "Patient Journey Assessment",
      desc: "We evaluate the full patient journey from scheduling through follow-up to identify gaps, friction points, and areas where the experience is breaking down.",
      includes: [
        "Patient journey mapping",
        "Touchpoint reviews",
        "Communication gap analysis",
        "Experience audits across departments",
      ],
    },
    {
      icon: CalendarClock,
      title: "Scheduling and Access Optimization",
      desc: "We help improve how patients access care by making scheduling, appointment communication, and pre-visit processes easier to navigate.",
      includes: [
        "Scheduling workflow review",
        "Appointment confirmation process improvements",
        "Reminder sequence recommendations",
        "Access and communication improvements before the visit",
      ],
    },
    {
      icon: ClipboardList,
      title: "Patient Intake Process Improvement",
      desc: "We help practices streamline intake so patients can move through registration, paperwork, and check-in with less confusion and less frustration.",
      includes: [
        "Intake workflow review",
        "Form and documentation process improvements",
        "Check-in experience evaluation",
        "Recommendations to reduce repetitive or unclear steps",
      ],
    },
    {
      icon: MessageSquare,
      title: "Patient Education and Communication Support",
      desc: "We help strengthen how information is shared with patients so they better understand their visit, treatment plan, and next steps.",
      includes: [
        "Patient education workflow review",
        "After-visit communication improvements",
        "Care plan communication support",
        "Follow-up instruction standardization",
      ],
    },
    {
      icon: HandHelping,
      title: "Patient Advocacy and Support Workflow Design",
      desc: "We help practices create better systems for answering patient questions, resolving concerns, and guiding patients through the care process in a more supportive way.",
      includes: [
        "Patient support workflow design",
        "Escalation and service recovery processes",
        "Advocacy touchpoint recommendations",
        "Coordination improvements between staff roles",
      ],
    },
    {
      icon: RefreshCw,
      title: "Retention and Follow-Up Strategy",
      desc: "We help practices build stronger follow-up systems that support continuity of care and keep patients engaged after the visit.",
      includes: [
        "Recall process development",
        "Follow-up outreach workflows",
        "Continuity of care communication plans",
        "Patient re-engagement strategies",
      ],
    },
  ];

  const outcomes = [
    "Clearer communication before and after visits",
    "Smoother intake and appointment flow",
    "Better patient understanding of next steps",
    "More consistent support throughout the care journey",
    "Stronger retention and continuity of care",
    "A better overall patient experience",
  ];

  const whoIsFor = [
    { icon: Building2, label: "Practices with inconsistent patient communication" },
    { icon: Heart, label: "Clinics experiencing friction in scheduling, intake, or follow-up" },
    { icon: Users, label: "Organizations that want to improve patient satisfaction and retention" },
    { icon: TrendingUp, label: "Growing practices that need a more consistent patient journey" },
  ];

  return (
    <div>
      <SEO
        title="Patient Experience Solutions for Healthcare Practices"
        description="Great care should feel clear, supportive, and consistent at every step. RNB Consulting Group helps healthcare organizations improve the systems, communication, and workflows that shape the patient experience."
        keywords="patient experience consulting, improve patient satisfaction healthcare, patient retention strategies clinic, healthcare patient journey mapping, patient intake improvement, patient communication healthcare, patient follow-up systems"
      />
      <StructuredData data={[{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.rnbconsultinggroup.com" },
          { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://www.rnbconsultinggroup.com/services" },
          { "@type": "ListItem", "position": 3, "name": "Patient Experience", "item": "https://www.rnbconsultinggroup.com/services/patient-experience" }
        ]
      }]} />

      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-rnb-navy via-rnb-navy to-rnb-blue overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-20 w-72 h-72 bg-rnb-teal/15 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-rnb-blue/20 rounded-full blur-3xl" />
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-8">
                <Heart className="w-4 h-4 text-rnb-teal" />
                <span className="text-sm font-medium">Patient Experience Solutions</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6" data-testid="patient-exp-hero-headline">
                <span className="block mb-2 bg-gradient-to-r from-rnb-teal to-cyan-300 bg-clip-text text-transparent">
                  A Better Patient Experience Builds Better Care.
                </span>
              </h1>

              <p className="text-base text-white/70 leading-relaxed mb-8">
                Patient experience is not just about bedside manner. It includes how patients schedule, what they are told before they arrive, how intake is handled, whether they understand their next steps, and how supported they feel throughout the care journey. We help healthcare organizations improve the systems, communication, and workflows that shape the patient experience from first contact through follow-up.
              </p>

              <ul className="flex flex-wrap gap-x-6 gap-y-3 mb-10 text-white/90 text-sm sm:text-base">
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0" /> Improve patient satisfaction</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0" /> Increase retention</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0" /> Strengthen trust and continuity of care</li>
              </ul>

              <Link to="/consultation">
                <Button
                  size="lg"
                  className="bg-rnb-teal hover:bg-rnb-teal/90 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-glow hover:shadow-glow-lg transition-all duration-300 hover:-translate-y-1"
                  data-testid="patient-exp-hero-cta"
                >
                  Schedule a Free Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <img
                src="/images/healthcare/doctor-patient-checkup.jpg"
                alt="Doctor consulting with patient in a warm clinical setting"
                className="rounded-2xl shadow-2xl w-full h-56 sm:h-72 lg:h-auto object-cover border-2 border-white/10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Sound Familiar */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              Sound Familiar?
            </h2>
            <p className="text-rnb-gray text-lg max-w-2xl mx-auto">
              These are some of the most common patient experience issues we help practices solve.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {[
              "Patients are confused before appointments because communication is inconsistent",
              "Intake feels repetitive, disorganized, or frustrating for patients",
              "Patients do not fully understand their care plan, next steps, or follow-up instructions",
              "Scheduling, wait times, and visit flow are creating unnecessary friction",
              "Patients feel like they are being moved through a process instead of genuinely supported",
              "Your team is delivering strong care, but the patient journey still feels disconnected",
            ].map((problem, idx) => (
              <div key={problem.slice(0, 40)} className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-100">
                <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-orange-500 text-xs font-bold">{idx + 1}</span>
                </div>
                <p className="text-rnb-gray leading-relaxed">{problem}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Patient Experience Services */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              Our Patient Experience Services
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((item, idx) => (
              <Card key={item.title || item.name || item.label || idx} className="border-0 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-7">
                  <div className="w-12 h-12 bg-rnb-light rounded-xl flex items-center justify-center mb-5">
                    <item.icon className="w-6 h-6 text-rnb-blue" />
                  </div>
                  <h3 className="text-lg font-bold text-rnb-navy mb-2">{item.title}</h3>
                  <p className="text-rnb-gray text-sm leading-relaxed mb-4">{item.desc}</p>
                  <div>
                    <p className="text-xs font-semibold text-rnb-navy uppercase tracking-wide mb-2">May include:</p>
                    <ul className="space-y-1.5">
                      {item.includes.map((inc, i) => (
                        <li key={inc.title || inc.name || `inc-${i}`} className="flex items-start gap-2 text-sm text-rnb-gray">
                          <CheckCircle className="w-3.5 h-3.5 text-rnb-teal flex-shrink-0 mt-0.5" />
                          {inc}
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Mid-page CTA */}
      <MidPageCTA variant={1} image="/images/healthcare/receptionist-patient.jpg" />

      {/* What This Helps Improve */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              What This Helps Improve
            </h2>
            <p className="text-rnb-gray text-lg max-w-2xl mx-auto">
              Our patient experience consulting is designed to help practices improve the parts of the care journey that most directly affect satisfaction, trust, and retention.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {outcomes.map((outcome, idx) => (
              <div key={outcome.slice(0, 40)} className="flex items-start gap-3 p-4">
                <CheckCircle className="w-5 h-5 text-rnb-teal flex-shrink-0 mt-0.5" />
                <span className="text-rnb-navy font-medium">{outcome}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              Who This Is For
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {whoIsFor.map((item, idx) => (
              <div key={item.title || item.name || item.label || idx} className="flex items-center gap-4 p-5 bg-white rounded-xl shadow-sm">
                <div className="w-11 h-11 bg-rnb-light rounded-xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-rnb-blue" />
                </div>
                <span className="text-rnb-navy font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Practices Choose RNB */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
              Why Practices Choose RNB
            </h2>
          </div>

          <div className="max-w-3xl mx-auto bg-slate-50 rounded-2xl p-8 border border-slate-100">
            <p className="text-rnb-gray leading-relaxed mb-4">
              We do more than point out patient experience issues. We help practices identify where the breakdowns are happening and improve the workflows behind them.
            </p>
            <p className="text-rnb-gray leading-relaxed">
              That means looking at concrete parts of the journey like scheduling, intake, education, advocacy, and follow-up, so the patient experience is not left to chance.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-rnb-navy to-rnb-blue relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-rnb-teal/15 rounded-full blur-3xl" />
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Improve Your Patient Experience?
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
            Let&apos;s talk about where your patient journey could be stronger and how we can help make it more clear, consistent, and supportive.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/consultation">
              <Button
                size="lg"
                className="bg-white text-rnb-navy hover:bg-white/90 rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:-translate-y-1 transition-all duration-300"
                data-testid="patient-exp-bottom-cta"
              >
                Book a Free Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/quote">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300"
              >
                Get an Instant Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PatientExperience;
